import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { generateSessionSettingsQueryOptions } from '@/hooks/sessions/use-session-settings';
import type { ConversationType } from '@/models/conversations/conversation-session';
import type { RespondedType, ResponseError } from '@/models/response';
import { useConversationSessionStore } from '@/stores/conversation-session';
import { addToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';
import { generateSessionKey } from '@/utils/session';

export default function useUpdateSessionTop(type: ConversationType, id: number | null | undefined) {
  const queryClient = useQueryClient();

  return useMutation<RespondedType, ResponseError, boolean>({
    mutationKey: ['/UpdateSessionTop', type, id],
    mutationFn: (params) =>
      fetcher(
        '/UpdateSessionTop',
        {
          [type === 'session' ? 'ChatSessionID' : 'GroupID']: id,
          IsTop: Number(params),
        },
        { validResponseCodes: [0] },
      ),
    onSuccess: async (_, params) => {
      if (!id) return;

      const key = generateSessionKey(type, id);
      useConversationSessionStore.getState().updateSession(key, { IsTop: params });
      queryClient.setQueryData(generateSessionSettingsQueryOptions(type, id).queryKey, (state) =>
        produce(state, (draft) => {
          if (draft) draft.IsTop = params;
        }),
      );
    },
    onError: undefined,
    onSettled: (data, error) =>
      addToast({
        type: data ? 'success' : 'error',
        message: data?.Msg ?? error?.message,
      }),
  });
}
